import { useTranslation } from 'react-i18next';
import { isUndefined, noop } from 'lodash';
import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { Button } from 'components/Button';
import { Heading } from 'components/Typography';
import { StackedRadioCardGroup } from 'components/Forms';
import { TherapyTarget } from 'domains/signup/data/types';
import { therapyTargetOptions } from 'domains/signup/data/options';
import { SignupContext, withSignupMachineState } from 'domains/signup/signupFlow/context/signupContext';
import { ScreenerContext, withScreenerMachineState } from 'domains/signup/screener/context/screenerContext';
import { getScreenerForAge } from 'domains/signup/screener/data/screener';
import { LanguageSwitcher } from 'components/LanguageSwitcher';

type Props = {
  defaultValue?: string;
  onSubmit: (value: string) => void;
};

const EnterTarget: React.FC<Props> = ({ defaultValue, onSubmit }) => {
  const [value, setValue] = useState(defaultValue);

  const { t } = useTranslation();

  return (
    <>
      <LanguageSwitcher className="mb-24" />
      <Heading level="h2" className="mb-64 sm-d:mb-40">
        {t('signup.steps.enterTarget.title')}
      </Heading>

      <StackedRadioCardGroup
        label={t('screener.questions.singleSelectTitle')}
        className="gap-14"
        disabled={false}
        showSelectedDot
        options={therapyTargetOptions}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value as TherapyTarget)}
        name="select-target"
        clearValue={noop}
        setError={noop}
        setValue={noop}
        tFunc={t}
        tPrefixKey="dropdowns.screener.select-target"
      />

      <Button
        data-testid="therapy-target-submit-button"
        size="lg"
        className="mt-64 sm-d:mt-40 sm-d:w-full"
        onClick={() => {
          onSubmit(value as string);
        }}
        disabled={isUndefined(value)}
      >
        {t('signup.steps.enterTarget.submit')}
      </Button>
    </>
  );
};

const SignupWrapper = () => {
  const target = SignupContext.useSelector(({ context }) => context.target);
  const { send } = SignupContext.useActorRef();

  const onSubmit = useCallback(
    (value: string) => {
      send({
        type: 'submitTarget',
        target: value as TherapyTarget,
      });
    },
    [send],
  );

  return <EnterTarget defaultValue={target} onSubmit={onSubmit} />;
};

export const SignupEnterTarget = withSignupMachineState(SignupWrapper, 'enterTarget');

const ScreenerWrapper = () => {
  const target = ScreenerContext.useSelector(({ context }) => context.target);
  const { send } = ScreenerContext.useActorRef();

  useEffectOnce(() => {
    getScreenerForAge(65);
  });

  const onSubmit = useCallback(
    (value: string) => {
      send({
        type: 'submitTarget',
        target: value as TherapyTarget,
      });
    },
    [send],
  );

  return <EnterTarget defaultValue={target} onSubmit={onSubmit} />;
};

export const ScreenerEnterTarget = withScreenerMachineState(ScreenerWrapper, 'enterTarget');
